<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.index,
                    },
                    {
                        name: activity.title,
                        href: null,
                    },
                ]"
            />
        </template>

        <div v-if="meta.info.duel_status_info.id" class="flex justify-center">
            <div>
                <AppCard>
                    <div class="px-2 py-4">
                        <div class="flex justify-center">
                            <span>{{ $tc('messages.duels', 1) }}</span>
                        </div>
                        <div>
                            <div class="flex justify-center items-center gap-4">
                                <div class="flex flex-col items-center">
                                    <img
                                        :src="meta.info.duel_status_info.challenger_avatar"
                                        :alt="meta.info.duel_status_info.challenger_name"
                                        :title="meta.info.duel_status_info.challenger_name"
                                        class="rounded-full h-20"
                                    />
                                    <div>{{ meta.info.duel_status_info.challenger_name }}</div>
                                    <div>
                                        <BadgePill :color="meta.info.duel_status_info.challenger_status.color">
                                            {{ meta.info.duel_status_info.challenger_status.display }}
                                        </BadgePill>
                                    </div>
                                    <div>{{ meta.info.duel_status_info.challenger_result }}</div>
                                </div>
                                <div><IconVs class="h-20 w-20 text-gray-300" /></div>
                                <div class="flex flex-col items-center">
                                    <img
                                        :src="meta.info.duel_status_info.challenged_avatar"
                                        :alt="meta.info.duel_status_info.challenged_name"
                                        :title="meta.info.duel_status_info.challenged_name"
                                        class="rounded-full h-20"
                                    />
                                    <div>{{ meta.info.duel_status_info.challenged_name }}</div>
                                    <div>
                                        <BadgePill :color="meta.info.duel_status_info.challenged_status.color">
                                            {{ meta.info.duel_status_info.challenged_status.display }}
                                        </BadgePill>
                                    </div>
                                    <div>{{ meta.info.duel_status_info.challenged_result }}</div>
                                </div>
                            </div>
                            <div v-if="meta.info.duel_status_info.tie_display_message">
                                <Tippy>
                                    <button>
                                        <IconInfoCircle class="w-6 h-6 text-gray-400" />
                                    </button>
                                    <template #content>
                                        {{ meta.info.duel_status_info.tie_display_message }}
                                    </template>
                                </Tippy>
                            </div>
                        </div>
                    </div>
                </AppCard>
            </div>
        </div>

        <div v-if="!meta.info.duel_status_info.is_in_progress" class="mt-3">
            <div class="md:flex justify-between">
                <div></div>
                <div>
                    <div v-if="activity.gradePill">
                        <GradePill :grade-pill="activity.gradePill" />
                    </div>
                </div>
            </div>
            <AppWellColorful class="mt-3">
                <div v-if="activity.tag">
                    <AppBanner v-if="quiz" class="mt-3 bg-gradient-to-r from-purple-600 rounded-2xl">
                        <div class="flex flex-col items-center align-middle justify-center">
                            <div class="text-xl">{{ activity.tag.level_summary.title }}</div>
                        </div>
                    </AppBanner>

                    <div class="mt-3 grid grid-cols-5 gap-4">
                        <AppCard class="col-span-2">
                            <div>
                                <div class="flex justify-center">
                                    <div>{{ $tc('messages.questions', 2) }}</div>
                                </div>
                                <div class="flex gap-2 justify-between">
                                    <div>
                                        <div
                                            class="p-1 rounded-lg text-center"
                                            :class="{
                                                'bg-green-300': activity.tag.level_summary.attempt_passing,
                                                'bg-red-300': !activity.tag.level_summary.attempt_passing,
                                            }"
                                        >
                                            {{ activity.tag.level_summary.activity_score }}
                                        </div>
                                        <div>
                                            {{ $tc('messages.activities', 1) }}
                                        </div>
                                    </div>
                                    <div v-if="activity.tag.level_summary.is_reached !== null">
                                        <div
                                            class="p-1 rounded-lg text-center"
                                            :class="{
                                                'bg-green-300': activity.tag.level_summary.is_reached === true,
                                                'bg-red-300': activity.tag.level_summary.is_reached === false,
                                            }"
                                        >
                                            {{ activity.tag.level_summary.level_score }}
                                            <span> ({{ activity.tag.level_summary.level_percent }}%) </span>
                                        </div>
                                        <div>
                                            {{ $t('total') }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </AppCard>
                        <AppCard>
                            <div>
                                <div class="flex justify-center">
                                    <div>{{ $tc('messages.levels', 1) }}</div>
                                </div>
                                <div>
                                    <div
                                        class="p-1 rounded-lg text-center"
                                        :class="{
                                            'bg-gray-200': activity.tag.level_summary.is_reached === null,
                                            'bg-green-300': activity.tag.level_summary.is_reached === true,
                                            'bg-red-300': activity.tag.level_summary.is_reached === false,
                                        }"
                                    >
                                        {{ activity.tag.level_summary.reached_status }}
                                    </div>
                                    <div class="text-xs text-gray-600">
                                        {{ activity.tag.level_summary.reached_status_info }}
                                    </div>
                                </div>
                            </div>
                        </AppCard>
                        <div class="col-span-2">
                            <AppCard>
                                <div class="flex justify-center">
                                    <div>{{ $t('messages.recommendation') }}</div>
                                </div>
                                <div class="flex gap-2 justify-between">
                                    <div v-if="activity.tag.level_summary.retry_links.previous_level">
                                        <div>
                                            <AppButton
                                                :href="activity.tag.level_summary.retry_links.previous_level.url"
                                                desired-action
                                            >
                                                {{ activity.tag.level_summary.retry_links.previous_level.text }}
                                            </AppButton>
                                            <div>
                                                {{ activity.tag.level_summary.retry_links.previous_level.difficulty }}
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="activity.tag.level_summary.retry_links.same_level">
                                        <div>
                                            <AppButton
                                                :href="activity.tag.level_summary.retry_links.same_level.url"
                                                desired-action
                                            >
                                                {{ activity.tag.level_summary.retry_links.same_level.text }}
                                            </AppButton>
                                            <div>
                                                {{ activity.tag.level_summary.retry_links.same_level.difficulty }}
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="activity.tag.level_summary.retry_links.next_level">
                                        <div>
                                            <AppButton
                                                :href="activity.tag.level_summary.retry_links.next_level.url"
                                                desired-action
                                            >
                                                {{ activity.tag.level_summary.retry_links.next_level.text }}
                                            </AppButton>
                                            <div>
                                                {{ activity.tag.level_summary.retry_links.next_level.difficulty }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </AppCard>
                        </div>
                    </div>
                </div>

                <AppBanner v-if="quiz" class="mt-3 bg-gradient-to-r from-purple-600 rounded-2xl">
                    <div class="flex flex-col items-center align-middle justify-center">
                        <div class="text-xl">{{ $t('messages.activity_completed!') }}</div>
                        <div class="text-lg mt-1 text-indigo-100">
                            {{ $t('messages.below_we_tell_you_how_it_went') }}
                        </div>
                    </div>
                </AppBanner>

                <div class="flex justify-center mt-3" v-if="activity.tag">
                    <div>
                        <div>
                            <span>
                                {{ activity.tag.practice_result_summary }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="md:grid md:grid-cols-3 md:gap-6 mt-3">
                    <AppCard>
                        <div class="flex justify-center">
                            <ZoomableImage
                                :src="student.avatar_or_default"
                                v-if="student.avatar_or_default"
                                class="flex-none"
                            >
                                <img
                                    class="relative z-30 inline-block w-32 h-32 rounded-full ring-2 ring-white"
                                    :src="student.avatar_or_default"
                                    alt="profile photo"
                                />
                            </ZoomableImage>
                        </div>
                    </AppCard>
                    <AppCard v-if="quiz || activity.feedback_info">
                        <div v-if="quiz">
                            <div>{{ $tc('messages.questions', 2) }}</div>
                            <div class="flex flex-wrap gap-2">
                                <a
                                    v-for="(question, index) in quiz.questions"
                                    :key="question.question_id"
                                    :href="`#question_${question.question_id}`"
                                >
                                    <div class="text-center text-sm text-gray-600">
                                        {{ index + 1 }}
                                    </div>
                                    <div>
                                        <CorrectIncorrectIcon :is-correct="!!question.answers_correct_count" />
                                    </div>
                                </a>
                            </div>
                        </div>
                        <div class="mt-3" v-if="activity.feedback_info">
                            {{ activity.feedback_info.feedback }}
                        </div>
                    </AppCard>
                    <AppCard v-if="activity.feedback_info">
                        <div class="flex justify-center">
                            <img :src="activity.feedback_info.reaction_image" alt="" class="w-32 h-32" />
                        </div>
                    </AppCard>
                </div>

                <div v-if="activity.tag" class="mt-5">
                    <AppBanner class="bg-gradient-to-r from-purple-600 rounded-2xl">
                        <div class="flex justify-center text-lg">
                            <span>{{ $t('messages.level_validation_status') }}</span>
                        </div>
                    </AppBanner>

                    <div v-if="activity.tag.expected_level" class="flex gap-2 items-center mt-3">
                        <IconTargetArrow class="text-indigo-100 w-12 h-12" />
                        <span
                            v-html="
                                $t('messages.remember_expected_level_to_reach', { level: activity.tag.expected_level })
                            "
                        />

                        <span> {{ activity.tag.level_reached_explanation }}</span>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <AppCard
                            v-for="mode in activity.tag.modes"
                            :key="mode.mode"
                            :class="{ 'lg:opacity-50 hover:opacity-100': !mode.is_current_mode }"
                        >
                            <div
                                class="p-2 rounded-2xl"
                                :class="[mode.is_current_mode ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100']"
                            >
                                {{ mode.mode }}
                            </div>
                            <div class="mt-3 flex justify-center gap-2 items-center">
                                <MedallionLevelsInfo :levels-info="mode" :size="mode.is_current_mode ? 'lg' : 'md'" />
                                <ActivityModeIcon
                                    :mode="mode.mode_code"
                                    :class="[
                                        mode.is_current_mode ? 'text-indigo-300 w-20 h-20' : 'text-gray-400 w-10 h-10',
                                    ]"
                                />
                            </div>
                        </AppCard>
                    </div>

                    <div class="mt-3" v-if="activity.tag?.report">
                        <AppCard>
                            <div>
                                {{ activity.tag.report.feedback }}
                            </div>
                            <div v-if="activity.tag.report.questions.length">
                                <PageSectionCollapsible>
                                    <template v-slot:title>
                                        {{ $tc('messages.questions', 2) }}
                                    </template>
                                    <template v-slot:subtitle>
                                        {{ activity.tag.report.subtitle }}
                                    </template>
                                    <div>
                                        {{
                                            $t('messages.tag_level_report_explanation', {
                                                count: activity.tag.report.questions.length,
                                            })
                                        }}
                                    </div>
                                    <div>
                                        <div class="flex flex-wrap gap-2">
                                            <div
                                                v-for="(question, index) in activity.tag.report.correct_map"
                                                :key="question.question_id"
                                            >
                                                <div class="text-center text-sm text-gray-600">
                                                    {{ index + 1 }}
                                                </div>
                                                <div>
                                                    <CorrectIncorrectIcon :is-correct="!!question.is_correct" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="pt-3">
                                        <div
                                            v-for="question in activity.tag.report.questions"
                                            :key="question.id"
                                            class="mt-1"
                                        >
                                            <QuestionReviewDisplay :students="quiz.students" :question="question" />
                                        </div>
                                    </div>
                                </PageSectionCollapsible>
                            </div>
                        </AppCard>
                    </div>

                    <div class="mt-3">
                        <AppCard>
                            <div>
                                {{ activity.tag.group_graph_explanation }}
                            </div>
                            <div>
                                <apexchart
                                    :type="activity.tag.graph.chart.type"
                                    :height="activity.tag.graph.chart.height"
                                    :series="activity.tag.graph.series"
                                    :options="activity.tag.graph"
                                />
                            </div>
                        </AppCard>
                    </div>
                </div>
            </AppWellColorful>

            <AppBanner class="mt-3" v-if="quiz">
                <div class="flex justify-center text-lg">
                    <span>{{ $t('messages.your_questions_answer_details') }}</span>
                </div>
            </AppBanner>
        </div>

        <div>
            <div class="mt-3 flex justify-end" v-if="meta.links.itinerary_show">
                <AppButton desired-action :href="meta.links.itinerary_show">
                    {{ $t('messages.return_to_itinerary') }}
                </AppButton>
            </div>

            <div class="mt-3">
                <AppWell v-if="activity.feedback || activity.rubric">
                    <div v-if="activity.feedback">
                        {{ activity.feedback }}
                        <div class="flex justify-end">
                            <span>
                                {{ activity.teacher_name }}
                            </span>
                        </div>
                    </div>

                    <div class="mt-5" v-if="activity.rubric">
                        <RubricResponseShow :rubric="activity.rubric" :ranges="activity.ranges" />
                    </div>
                </AppWell>
            </div>
        </div>

        <div v-if="activity.materials.length > 0" class="mt-3 md:mt-5">
            <div>
                {{ $tc('messages.materials', 2) }}
            </div>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <ul class="">
                        <li v-for="(row, index) in activity.materials" :key="index" class="list-disc">
                            <span v-if="!row.url">
                                {{ row.title }}
                            </span>
                            <a :href="row.url" target="_blank" class="text-indigo-500" v-else>
                                <svg
                                    class="w-4 h-4 inline-block"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"
                                    ></path>
                                    <path
                                        d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"
                                    ></path>
                                </svg>
                                <span class="truncate text-ellipsis overflow-hidden">
                                    {{ row.title ? row.title : row.url }}
                                </span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <div v-if="meta.info.duel_status_info.is_in_progress">
            {{ $t('messages.duel_in_progress_message') }}
        </div>

        <QuizReview
            :activity="activity"
            :questions="quiz.questions"
            :students="quiz.students"
            :mode="quiz.mode"
            v-if="quiz"
        />
        <div v-if="meta.info.duel_status_info.id" class="mt-3 flex gap-x-2 items-center">
            <AppButton element="InertiaLink" :href="meta.links.duels_index" desired-action>
                {{ $tc('messages.duels', 2) }}
            </AppButton>

            <DuelPartnerSearchPanel :endpoint-url="meta.links.practice_duels_search" />
        </div>
        <div v-else>
            <div v-if="activity.practice.retries" class="mt-5">
                <div class="w-full border-t border-gray-300" />
                <div class="md:flex gap-4 mt-5">
                    <div>
                        <div class="">{{ $t('messages.keep_practicing') }}</div>

                        <div class="flex gap-x-2 mt-5">
                            <AppButton element="InertiaLink" :href="activity.practice.subject.url" desired-action>
                                {{ activity.practice.subject.text }}
                            </AppButton>
                            <div v-for="retry in activity.practice.retries" :key="retry.url">
                                <AppButton element="InertiaLink" :href="retry.url" desired-action>
                                    {{ retry.text }}
                                </AppButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="tag" class="mt-3">
            <div>
                {{ $tc('messages.links', 2) }}
            </div>
            <div class="my-1">
                <div v-for="link in tag.links" :key="link.url">
                    <div>
                        <a :href="link.url" class="text-indigo-500">
                            {{ link.title }}
                        </a>
                    </div>
                    <div class="text-gray-700 text-sm mt-1" v-if="link.description">
                        {{ link.description }}
                    </div>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <div class="relative z-20 pointer-events-none">
                <div class="fixed z-10 inset-0">
                    <div class="flex items-center justify-center min-h-full p-4">
                        <ConfettiExplosion v-if="showConfetti" />
                    </div>
                </div>
            </div>
        </Teleport>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppWell from '@/Shared/AppWell.vue';
import RubricResponseShow from '@/Pages/Teacher/Activities/Rubrics/RubricResponseShow.vue';
import QuizReview from '@/components/QuizReview.vue';
import AppButton from '@/components/AppButton.vue';
import Modal from '@/components/Modal.vue';
import GradePill from '@/Pages/Student/Activity/GradePill.vue';
import ConfettiExplosion from 'vue-confetti-explosion';
import MedallionLevelsInfo from '@/components/MedallionLevelsInfo.vue';
import AppCard from '@/Shared/AppCard.vue';
import ActivityModeIcon from '@/components/Quiz/ActivityModeIcon.vue';
import ZoomableImage from '@/components/ZoomableImage.vue';
import CorrectIncorrectIcon from '@/components/Quiz/CorrectIncorrectIcon.vue';
import BadgePill from '@/components/BadgePill.vue';
import { IconInfoCircle, IconTargetArrow, IconVs } from '@tabler/icons-vue';
import DuelPartnerSearchPanel from '@/Pages/Student/Activity/Practice/Duels/partials/DuelPartnerSearchPanel.vue';
import AppBanner from '@/Shared/AppBanner.vue';
import AppWellColorful from '@/Shared/AppWellColorful.vue';
import QuestionReviewDisplay from '../../../components/QuestionReviewDisplay.vue';
import PageSectionCollapsible from '../../../Shared/PageSectionCollapsible.vue';
import { Tippy } from 'vue-tippy';

export default {
    components: {
        Tippy,
        IconInfoCircle,
        PageSectionCollapsible,
        QuestionReviewDisplay,
        AppWellColorful,
        AppBanner,
        DuelPartnerSearchPanel,
        BadgePill,
        CorrectIncorrectIcon,
        ZoomableImage,
        ActivityModeIcon,
        AppCard,
        MedallionLevelsInfo,
        ConfettiExplosion,
        GradePill,
        Modal,
        AppButton,
        QuizReview,
        PanelPage,
        AppWell,
        RubricResponseShow,
        IconVs,
        IconTargetArrow,
    },
    layout: Layout,
    name: 'Show',
    props: ['activity', 'quiz', 'student', 'tag', 'meta'],
    data() {
        return {
            showConfetti: false,
        };
    },
    mounted() {
        if (this.activity.scoreModal.show_modal) {
            if (this.activity.scoreModal.is_top_score) {
                setTimeout(() => {
                    this.showConfetti = true;
                }, 1600);
            }
        }
    },
};
</script>
